import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import {
  Paper,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Alert
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import EntityForm from "../../components/EntityForm";
import api from "../../services/api";
import { renderFormField } from "../../components/FormFields";
import { FormField, Stable, Tenant } from "../../interfaces";
import { useCryptoNavigation, useAppNavigation } from "../../App";

export default function StableForm() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation();
  const { getParam } = useAppNavigation();

  const id = getParam("id");
  const mode = getParam("mode");
  const tenantIdParam = getParam("tenantId");

  // A rota de edição nem sempre repassa o tenantId (viraria `/stable/undefined/:id`
  // e a API falha). Resolvemos com a MESMA fonte de verdade da StableList: o tenant
  // selecionado no seletor da top bar (localStorage 'selectedTenantId'), com fallback
  // para o tenant do próprio usuário.
  const resolvedTenantId =
    tenantIdParam ||
    localStorage.getItem("selectedTenantId") ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "null");
        const tid = u?.tenantId ?? u?.tenant_id;
        return tid != null ? String(tid) : undefined;
      } catch {
        return undefined;
      }
    })() ||
    undefined;

  const isEditMode = mode === "edit" && Boolean(id);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(
    resolvedTenantId ? Number(resolvedTenantId) : null
  );
  const [stableTenantId, setStableTenantId] = useState<number | null>(null);
  const [stableName, setStableName] = useState<string>("");
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // === 1️⃣ Carregar Tenants ===
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await api.get("/tenant");

        let tenantData: Tenant[] = [];
        if (Array.isArray(response.data)) {
          tenantData = response.data;
        } else if (response.data?.tenants && Array.isArray(response.data.tenants)) {
          tenantData = response.data.tenants;
        } else if (typeof response.data === "object") {
          const possibleArrays = Object.values(response.data).filter(
            (value) => Array.isArray(value) && value.length > 0
          );
          if (possibleArrays.length > 0) tenantData = possibleArrays[0] as Tenant[];
        }

        setTenants(tenantData);
      } catch (err) {
        console.error("Erro ao carregar tenants:", err);
        setError(t("stables.errorLoadOwners"));
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, []);

  // === 2️⃣ Buscar estábulo atual quando tenants carregarem ===
  useEffect(() => {
    if (!isEditMode || !id || tenants.length === 0) return;

    const fetchStable = async () => {
      try {
        const response = await api.get(`/stable/${resolvedTenantId}/${id}`);
        const stableData = response.data?.stable || response.data;

        const tenantId =
          stableData?.stableTenant ||
          stableData?.tenantId ||
          selectedTenantId ||
          (tenants.length > 0 ? tenants[0].tenantId : null);

        if (tenantId) {
          setStableTenantId(tenantId);
          setSelectedTenantId(tenantId);
        }

        if (stableData?.stableName || stableData?.name) {
          setStableName(stableData.stableName || stableData.name);
        }

        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        console.error("Erro ao buscar estábulo:", error);
        setError(t("stables.errorLoadStable"));
      }
    };

    fetchStable();
  }, [isEditMode, id, tenants]);

  // === Campos do formulário ===
  const formFields: FormField[] = [
    {
      name: "stableName",
      label: t("stables.nameLabel"),
      type: "text",
      required: true,
      fullWidth: true,
      helperText: t("stables.nameHelper"),
    }
  ];

  // === 3️⃣ Transformações ===
  const transformBeforeSave = (data: any) => {
    const processed = { ...data };
    // Só manda o proprietário quando há um de verdade. Sem a guarda, uma
    // edição feita antes do contexto resolver gravaria 0/null por cima.
    const tenantIdValue = stableTenantId || selectedTenantId;
    if (tenantIdValue) {
      processed.stableTenant = tenantIdValue;
    }
    delete processed.tenantId;
    return processed;
  };

  const transformAfterLoad = (data: any): Stable => {
    const stableData = data?.stable || data;
    return {
      stableId: stableData?.stableId || stableData?.id || 0,
      stableName: stableData?.stableName || stableData?.name || "",
      tenantId:
        stableData?.stableTenant ||
        stableData?.tenantId ||
        stableTenantId ||
        selectedTenantId ||
        0,
    };
  };

  // === 4️⃣ Validação ===
  const validateForm = (formData: any): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formData.stableName?.trim())
      errors.stableName = t("stables.nameRequired");
    return errors;
  };

  // === 5️⃣ Navegação e Handlers ===
  const handleSuccess = (data: any) => console.log("✅ Estábulo salvo:", data);
  const handleError = (error: any) => {
    console.error("❌ Erro ao salvar estábulo:", error);
    setError(t("stables.errorSave"));
  };

  const handleBack = () => {
    navigateToPage("estabulos", {
      mode: "list",
      tenantId: resolvedTenantId || selectedTenantId,
    });
  };

  const getSelectedTenantName = (): string => {
    if (!selectedTenantId) return "";
    const tenant = tenants.find((t) => t.tenantId === selectedTenantId);
    return tenant ? tenant.tenantName : "";
  };

  // === 6️⃣ Render ===
  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                color="inherit"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBack();
                }}
              >
                {t("nav.stables")}
              </Link>
              <Typography color="text.primary">
                {isEditMode ? t("stables.editTitle") : t("stables.newTitle")}
              </Typography>
            </Breadcrumbs>
            <Typography variant="h5" component="h1" sx={{ mt: 1 }}>
              {isEditMode ? t("stables.editTitle") : t("stables.newTitle")}
            </Typography>
          </Box>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleBack}
          >
            {t("common.back")}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {selectedTenantId && getSelectedTenantName() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("stables.belongsTo")}{" "}
          <strong>{getSelectedTenantName()}</strong>
        </Alert>
      )}

      {isEditMode && stableName && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("stables.editingStable")} <strong>{stableName}</strong>
        </Alert>
      )}

      {!loading && (
        <EntityForm
          title=""
          endpoint={`/stable/${resolvedTenantId}`}
          fields={formFields}
          listPath={resolvedTenantId ? `/stable/${resolvedTenantId}` : "/stable"}
          listPageName="estabulos"
          renderField={renderFormField}
          transformBeforeSave={transformBeforeSave}
          transformAfterLoad={transformAfterLoad}
          validateForm={validateForm}
          onSuccess={handleSuccess}
          onError={handleError}
          idField="stableId"
          submitButtonText={t("stables.saveButton")}
          useEncryptedNavigation={true}
          defaultTenantId={resolvedTenantId}
          key={`stable-form-${refreshTrigger}`}
        />
      )}
    </>
  );
}
